import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useApi, useTenantContext } from '../lib/api';

type InboxQueue =
  | 'new'
  | 'urgent'
  | 'awaiting_human'
  | 'in_progress'
  | 'waiting_customer'
  | 'resolved_today';

type InboxCounters = Record<InboxQueue, number>;
type InboxStatusCounters = Partial<Record<string, number>>;

type CaseListItem = {
  case_id: string;
  customer_phone: string;
  customer_name?: string | null;
  unit_id?: string | null;
  unit_name?: string | null;
  status: string;
  priority: string;
  intent: string;
  updated_at: string;
  needs_human_attention: boolean;
  assigned_to?: string | null;
  active_handoff_id?: string | null;
};

type InboxFilters = {
  queue: InboxQueue | '';
  status: string;
  priority: string;
  intent: string;
  assigned_to: string;
  unit_id: string;
  sla: string;
  updated_from: string;
  updated_to: string;
  search: string;
  sort: 'last_message' | 'priority';
};

const QUEUES: Array<{ value: InboxQueue; label: string }> = [
  { value: 'new', label: 'Novos' },
  { value: 'urgent', label: 'Urgentes' },
  { value: 'awaiting_human', label: 'Aguardando humano' },
  { value: 'in_progress', label: 'Em atendimento' },
  { value: 'waiting_customer', label: 'Aguardando cliente' },
  { value: 'resolved_today', label: 'Resolvidos hoje' },
];

const INITIAL_FILTERS: InboxFilters = {
  queue: 'awaiting_human',
  status: '',
  priority: '',
  intent: '',
  assigned_to: '',
  unit_id: '',
  sla: '',
  updated_from: '',
  updated_to: '',
  search: '',
  sort: 'last_message',
};

const EMPTY_COUNTERS: InboxCounters = {
  new: 0,
  urgent: 0,
  awaiting_human: 0,
  in_progress: 0,
  waiting_customer: 0,
  resolved_today: 0,
};

const EMPTY_STATUS_COUNTERS: InboxStatusCounters = {};

const STATUS_OPTIONS = [
  { value: 'NEW', label: 'Novo' },
  { value: 'READY_FOR_STAFF', label: 'Aguardando humano' },
  { value: 'IN_STAFF_HANDS', label: 'Em atendimento' },
  { value: 'WAITING_CUSTOMER_INFO', label: 'Aguardando cliente' },
  { value: 'NEEDS_REVIEW', label: 'Revisao' },
  { value: 'CLOSED', label: 'Encerrado' },
];

const SLA_MINUTES = 30;

export function isSlaBreached(item: Pick<CaseListItem, 'updated_at' | 'status'>, now = new Date()) {
  if (['CLOSED', 'DONE', 'CANCELLED'].includes(item.status)) return false;
  return now.getTime() - new Date(item.updated_at).getTime() > SLA_MINUTES * 60_000;
}

export function caseCardClass(item: CaseListItem, now = new Date()) {
  return [
    'case-card',
    item.priority === 'urgent' ? 'case-card-urgent' : '',
    isSlaBreached(item, now) ? 'case-card-sla' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

function buildQuery(filters: InboxFilters, cursor?: string | null) {
  const qs = new URLSearchParams();
  qs.set('limit', '25');
  if (filters.queue) qs.set('queue', filters.queue);
  if (filters.status) qs.set('status', filters.status);
  if (filters.priority) qs.set('priority', filters.priority);
  if (filters.intent) qs.set('intent', filters.intent);
  if (filters.assigned_to.trim()) qs.set('assigned_to', filters.assigned_to.trim());
  if (filters.unit_id.trim()) qs.set('unit_id', filters.unit_id.trim());
  if (filters.sla) qs.set('sla', filters.sla);
  if (filters.updated_from) qs.set('updated_from', filters.updated_from);
  if (filters.updated_to) qs.set('updated_to', filters.updated_to);
  if (filters.search.trim()) qs.set('search', filters.search.trim());
  qs.set('sort', filters.sort);
  if (cursor) qs.set('cursor', cursor);
  return qs.toString();
}

function statusLabel(status: string) {
  const labels: Record<string, string> = {
    NEW: 'Novo',
    READY_FOR_STAFF: 'Aguardando humano',
    IN_STAFF_HANDS: 'Em atendimento',
    WAITING_CUSTOMER_INFO: 'Aguardando cliente',
    NEEDS_REVIEW: 'Revisao',
    CLOSED: 'Encerrado',
  };
  return labels[status] ?? status;
}

function priorityLabel(priority: string) {
  const labels: Record<string, string> = {
    urgent: 'Urgente',
    high: 'Alta',
    medium: 'Media',
    low: 'Baixa',
  };
  return labels[priority] ?? priority;
}

export default function InboxPage() {
  const { tenant_id, hasScope } = useTenantContext();
  const { request } = useApi();
  const [items, setItems] = useState<CaseListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [counters, setCounters] = useState<InboxCounters>(EMPTY_COUNTERS);
  const [statusCounters, setStatusCounters] = useState<InboxStatusCounters>(EMPTY_STATUS_COUNTERS);
  const [filters, setFilters] = useState<InboxFilters>(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<InboxFilters>(INITIAL_FILTERS);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    (cursor?: string | null) => {
      if (!tenant_id) return;
      let cancelled = false;
      setLoading(true);
      setError(null);
      const qs = buildQuery(appliedFilters, cursor);
      request<{
        items: CaseListItem[];
        next_cursor?: string | null;
        counters?: InboxCounters;
        status_counters?: InboxStatusCounters;
      }>(`/tenants/${encodeURIComponent(tenant_id)}/cases?${qs}`)
        .then((out) => {
          if (!cancelled) {
            setItems((current) => (cursor ? [...current, ...out.items] : out.items));
            setNextCursor(out.next_cursor ?? null);
            setCounters(out.counters ?? EMPTY_COUNTERS);
            setStatusCounters(out.status_counters ?? EMPTY_STATUS_COUNTERS);
          }
        })
        .catch((err) => {
          if (!cancelled) setError(String(err instanceof Error ? err.message : err));
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });

      return () => {
        cancelled = true;
      };
    },
    [appliedFilters, request, tenant_id],
  );

  useEffect(() => {
    return load(null);
  }, [load]);

  function submitFilters(event: FormEvent) {
    event.preventDefault();
    setAppliedFilters(filters);
    setNextCursor(null);
  }

  function selectQueue(queue: InboxQueue) {
    const next = { ...filters, queue };
    setFilters(next);
    setAppliedFilters(next);
    setNextCursor(null);
  }

  function clearFilters() {
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    setNextCursor(null);
  }

  if (!tenant_id) return <main className="page">Usuario sem tenant configurado.</main>;

  return (
    <main className="page">
      <header className="page-header">
        <div>
          <h1>Inbox</h1>
          <p>Atendimentos do tenant {tenant_id}</p>
        </div>
        {hasScope('METRICS_VIEW') || hasScope('CONFIG_CHANGE') || hasScope('USERS_ADMIN') ? (
          <div className="header-actions">
            {hasScope('METRICS_VIEW') ? (
              <Link className="nav-link" to="/metrics">
                Metricas
              </Link>
            ) : null}
            {hasScope('CONFIG_CHANGE') ? (
              <>
                <Link className="nav-link" to="/config">
                  Configuracao
                </Link>
                <Link className="nav-link" to="/simulation">
                  Simulacao
                </Link>
              </>
            ) : null}
            {hasScope('USERS_ADMIN') ? (
              <Link className="nav-link" to="/users">
                Usuarios
              </Link>
            ) : null}
          </div>
        ) : null}
      </header>

      <section className="queue-tabs" aria-label="Filas">
        {QUEUES.map((queue) => (
          <button
            key={queue.value}
            type="button"
            className={appliedFilters.queue === queue.value ? 'queue-tab active' : 'queue-tab'}
            onClick={() => selectQueue(queue.value)}
          >
            <span>{queue.label}</span>
            <strong>{counters[queue.value] ?? 0}</strong>
          </button>
        ))}
      </section>

      <form className="inbox-filters" onSubmit={submitFilters}>
        <label>
          Busca
          <input
            value={filters.search}
            onChange={(event) => setFilters({ ...filters, search: event.target.value })}
            placeholder="Telefone, nome ou case id"
          />
        </label>
        <label>
          Status
          <select
            value={filters.status}
            onChange={(event) => setFilters({ ...filters, status: event.target.value, queue: '' })}
          >
            <option value="">Todos</option>
            {STATUS_OPTIONS.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label} ({statusCounters[status.value] ?? 0})
              </option>
            ))}
          </select>
        </label>
        <label>
          Prioridade
          <select
            value={filters.priority}
            onChange={(event) =>
              setFilters({ ...filters, priority: event.target.value, queue: '' })
            }
          >
            <option value="">Todas</option>
            <option value="urgent">Urgente</option>
            <option value="high">Alta</option>
            <option value="medium">Media</option>
            <option value="low">Baixa</option>
          </select>
        </label>
        <label>
          Intencao
          <select
            value={filters.intent}
            onChange={(event) => setFilters({ ...filters, intent: event.target.value })}
          >
            <option value="">Todas</option>
            <option value="BOOKING">Agendamento</option>
            <option value="QUOTE">Orcamento</option>
            <option value="QUESTION">Duvida</option>
            <option value="CANCEL">Cancelamento</option>
            <option value="RESCHEDULE">Remarcacao</option>
            <option value="OTHER">Outra</option>
          </select>
        </label>
        <label>
          Atendente
          <input
            value={filters.assigned_to}
            onChange={(event) => setFilters({ ...filters, assigned_to: event.target.value })}
            placeholder="user id"
          />
        </label>
        <label>
          Unidade
          <input
            value={filters.unit_id}
            onChange={(event) => setFilters({ ...filters, unit_id: event.target.value })}
            placeholder="unit id"
          />
        </label>
        <label>
          SLA
          <select
            value={filters.sla}
            onChange={(event) => setFilters({ ...filters, sla: event.target.value })}
          >
            <option value="">Todos</option>
            <option value="breached">Vencido</option>
            <option value="ok">Dentro do prazo</option>
          </select>
        </label>
        <label>
          De
          <input
            type="date"
            value={filters.updated_from}
            onChange={(event) => setFilters({ ...filters, updated_from: event.target.value })}
          />
        </label>
        <label>
          Ate
          <input
            type="date"
            value={filters.updated_to}
            onChange={(event) => setFilters({ ...filters, updated_to: event.target.value })}
          />
        </label>
        <label>
          Ordenar
          <select
            value={filters.sort}
            onChange={(event) =>
              setFilters({ ...filters, sort: event.target.value as InboxFilters['sort'] })
            }
          >
            <option value="last_message">Ultima mensagem</option>
            <option value="priority">Prioridade</option>
          </select>
        </label>
        <div className="filter-actions">
          <button type="submit" disabled={loading}>
            Aplicar
          </button>
          <button type="button" className="secondary" onClick={clearFilters} disabled={loading}>
            Limpar
          </button>
        </div>
      </form>

      {loading ? <p>Carregando...</p> : null}
      {error ? <p className="error">{error}</p> : null}

      <section className="card-list">
        {items.map((item) => {
          const breached = isSlaBreached(item);
          return (
            <Link className={caseCardClass(item)} key={item.case_id} to={`/cases/${item.case_id}`}>
              <div className="case-card-header">
                <strong>{item.customer_name ?? item.customer_phone}</strong>
                <span>{priorityLabel(item.priority)}</span>
              </div>
              <span>Telefone: {item.customer_phone}</span>
              <span>Status: {statusLabel(item.status)}</span>
              <span>Intencao: {item.intent}</span>
              <span>Unidade: {item.unit_name ?? item.unit_id ?? 'Sem unidade'}</span>
              <span>Responsavel: {item.assigned_to ?? 'Sem responsavel'}</span>
              <span>Atualizado em: {new Date(item.updated_at).toLocaleString()}</span>
              <div className="case-badges">
                {item.needs_human_attention ? <em>Humano</em> : null}
                {item.priority === 'urgent' ? <em>Urgente</em> : null}
                {breached ? <em>SLA vencido</em> : null}
              </div>
            </Link>
          );
        })}
      </section>

      {!loading && !items.length ? <p className="hint">Nenhum atendimento encontrado.</p> : null}

      {nextCursor ? (
        <div className="action-row">
          <button type="button" disabled={loading} onClick={() => load(nextCursor)}>
            {loading ? 'Carregando...' : 'Carregar mais'}
          </button>
        </div>
      ) : null}
    </main>
  );
}
